import axios, { AxiosRequestConfig, AxiosError, AxiosResponse, AxiosInstance } from "axios";
import { AuthService } from "@/services/auth-service";
import { ServerConfig } from "@/config/config";
import { tokenStore } from "./token-store";
import { MainSocketService } from "@/services/main-socket-service";
import { EventEmitter } from "./event-emitter";
import { HttpError } from "./services/http-error";


export function getAuthenticatedHeaders(): any {
	return {
		Authorization: tokenStore.getSelectedToken().token,
		SocketID: MainSocketService.getId()
	};
}

export const AxiosErrorEmitter = new EventEmitter();

export interface IAxiosCreateOptions {
    skipAuthentication?:boolean;
}

let isInitingDossier = false;
let isInitingUser = false;
let eventEmitter = new EventEmitter();

async function initDossier(){
	if (!isInitingDossier){
		isInitingDossier = true;
		try{
			await axios.post(`${ServerConfig.host}/dossier/init`, {Lang: "nl-be"}, {headers: getAuthenticatedHeaders()});
			eventEmitter.emit("dossier-init", null);
		}catch(err){
			eventEmitter.emit("dossier-init", err);
		}
		isInitingDossier = false;
	}else{
		await new Promise<void>((resolve, reject) => {
			eventEmitter.once("dossier-init", (err:any)=>{
				if (err){
					reject(err);
				}else{
					resolve();
				}
			});
		});
	}
}

async function initUser(){
	if (!isInitingUser){
		isInitingUser = true;
		try{
			await axios.post(`${ServerConfig.host}/user/init`, {Lang: "nl-be"}, {headers: getAuthenticatedHeaders()});
			eventEmitter.emit("user-init", null);
		}catch(err){
			eventEmitter.emit("user-init", err);
		}
		isInitingUser = false;
	}else{
		await new Promise<void>((resolve, reject)=>{
			eventEmitter.once("user-init", (err:any)=>{
				if (err){
					reject(err);
				}else{
					resolve();
				}
			});
		});
	}
}

async function checkFor600StatusCodes(value:AxiosError):Promise<boolean> {
	if (!value.response) throw value;
	if (value.response.status == 600) {
		await initDossier();
		await initUser();
		return true;
	}
	if (value.response.status == 601) {
		await initUser();
		AuthService.emit("user-init");
		return true;
	}
	if (value.response.status == 603) {
		let isSwitched = await new Promise<Boolean>((resolve) => {
			AuthService.emit("other-session-active", resolve);
		});
		if (!isSwitched) {
			let err = new HttpError({}, 603);
			err.key = "NOT_ACTIVE_SESSION";
			err.message = "Not active session";
			throw err;
		}
		return true;
	}
	return false;
}

export async function refreshTokensIfRequired(){
	if (!tokenStore.tokenExpires) return;
	if (tokenStore.willTokenExpireInSeconds(60)) {
		await AuthService.refreshTokens();
	}
}

async function authenticate(value:AxiosRequestConfig) {
	await refreshTokensIfRequired();
	let authHeaders = getAuthenticatedHeaders();
	if (!value.headers) {
		value.headers = {};
	}
	value.headers.Authorization = authHeaders.Authorization;
	if (value.url && value.url.startsWith(ServerConfig.host)) {
		value.headers.SocketID = authHeaders.SocketID;
	}
}

function addRequestInterceptors(client:AxiosInstance, options:IAxiosCreateOptions){
	client.interceptors.request.use(async (value: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
		if (!options.skipAuthentication){
			await authenticate(value);
		}
		if (!value.params) return value;
		for (let key of Object.keys(value.params)) {
			let t = typeof (value.params[key]);
			if (t == "object") {
				value.params[key] = JSON.stringify(value.params[key]);
			}
		}
		return value;
	});
}


// eslint-disable-next-line max-lines-per-function
function addResponseInterceptors(client:AxiosInstance){
	client.interceptors.response.use(undefined, async (value: AxiosError): Promise<AxiosError | AxiosResponse> => {
		if (!value.response) throw value;
		if (!value.config) throw value;
		if (value.config.responseType == "stream") {
			return value;
		}
		let has600 = await checkFor600StatusCodes(value);
		if (has600){
			return client.request(value.config);
		}

		if (typeof(value.response.data) == "object") {
			let err = new HttpError(value.response.data, value.response.status);
			err.isNetworkError = false;
			AxiosErrorEmitter.emit("error", err, value, client);
			throw err;
		}else if (typeof(value.response.data) == "string") {
			let err = new HttpError({message: value.response.data}, value.response.status);
			err.isNetworkError = false;
			AxiosErrorEmitter.emit("error", err, value, client);
			throw err;
		}

		let err = new HttpError({}, value.response.status);
		err.isNetworkError = true;
		err.message = value.message;
		err.key = "HTTP_ERROR";
		if (!value.response.status) {
			err.message = "Request failed";
			err.data = [value.message];
		} else if (!(value.response.status + "").match(/^2[0-9][0-9]$/)) {
			err.data = [`${value.response.data}`];
		}
		AxiosErrorEmitter.emit("error", err, value, client);
		throw err;
	});
}


export function createAxiosClient(options:IAxiosCreateOptions = {}):AxiosInstance {
	let client = axios.create();
	addRequestInterceptors(client, options);
	addResponseInterceptors(client);
	return client;
}


export const Axios = createAxiosClient();

export const UnauthenticatedClient = createAxiosClient({skipAuthentication: true});